// ═══════════════════════════════════════════════════════════════
// ZoneForm — Modal for creating/editing zone metadata
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import {
  Modal, TextInput, Select, Textarea, ColorInput, Slider,
  Button, Group, Stack, Text, Paper,
} from '@mantine/core';
import type { ZoneType, ZoneStatus, GeoOpsZone } from '../../store/geoopsStore';
import { ZONE_TYPE_META, DEFAULT_ZONE_COLORS } from '../../store/geoopsStore';
import { polygonArea, polylineLength, formatArea, formatDistance } from '../../lib/geoopsGeo';

interface ZoneFormProps {
  opened: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    zoneType: ZoneType;
    status: ZoneStatus;
    color: string;
    opacity: number;
    notes: string;
  }) => void;
  initialData?: Partial<GeoOpsZone>;
  title?: string;
  /** Geometry just drawn — used to display measurement summary at top */
  drawnGeometry?: GeoJSON.Geometry | null;
}

const TYPE_OPTIONS = Object.entries(ZONE_TYPE_META).map(([value, meta]) => ({
  value, label: meta.label,
}));

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'planned', label: 'Planned' },
  { value: 'resolved', label: 'Resolved' },
];

export default function ZoneForm({ opened, onClose, onSave, initialData, title, drawnGeometry }: ZoneFormProps) {
  const [name, setName] = useState('');
  const [zoneType, setZoneType] = useState<ZoneType>('impact_zone');
  const [status, setStatus] = useState<ZoneStatus>('active');
  const [color, setColor] = useState('#DC2626');
  const [opacity, setOpacity] = useState(0.3);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || '');
      setZoneType(initialData.zoneType || 'impact_zone');
      setStatus(initialData.status || 'active');
      setColor(initialData.color || DEFAULT_ZONE_COLORS[initialData.zoneType || 'impact_zone']);
      setOpacity(initialData.opacity ?? 0.3);
      setNotes(initialData.notes || '');
    } else {
      setName('');
      setZoneType('impact_zone');
      setStatus('active');
      setColor('#DC2626');
      setOpacity(0.3);
      setNotes('');
    }
  }, [initialData, opened]);

  // Auto-update color when zone type changes (if not manually set)
  const handleTypeChange = (val: string | null) => {
    const t = (val || 'custom') as ZoneType;
    setZoneType(t);
    setColor(DEFAULT_ZONE_COLORS[t] || '#6B7280');
  };

  const handleSubmit = () => {
    onSave({ name: name.trim() || 'Untitled Zone', zoneType, status, color, opacity, notes });
    onClose();
  };

  // Compute measurement summary for the just-drawn geometry
  const geom = drawnGeometry || initialData?.geometry || null;
  const measurement = (() => {
    if (!geom) return null;
    if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
      const area = polygonArea(geom.type === 'Polygon' ? geom : { type: 'Polygon', coordinates: geom.coordinates[0] });
      return { kind: 'area', label: 'Area', value: formatArea(area), shape: geom.type === 'Polygon' ? `${geom.coordinates[0].length - 1}-vertex polygon` : 'Multi-polygon' };
    }
    if (geom.type === 'LineString') {
      return { kind: 'length', label: 'Length', value: formatDistance(polylineLength(geom)), shape: `${geom.coordinates.length}-point line` };
    }
    return null;
  })();

  return (
    <Modal opened={opened} onClose={onClose} title={title || 'New Zone'} size="md">
      <Stack gap="sm">
        {measurement && (
          <Paper p="xs" radius="sm" withBorder style={{ background: '#F7F8FD', borderColor: '#4B3FFF' }}>
            <Group justify="space-between" gap="xs">
              <Text size="xs" c="dimmed">{measurement.shape}</Text>
              <Text size="sm" fw={600} c="#4B3FFF">
                {measurement.label}: {measurement.value}
              </Text>
            </Group>
          </Paper>
        )}

        <TextInput label="Zone Name" placeholder="e.g., Impact Zone Alpha" value={name}
          onChange={e => setName(e.currentTarget.value)} required />

        <Group grow>
          <Select label="Type" data={TYPE_OPTIONS} value={zoneType}
            onChange={handleTypeChange} />
          <Select label="Status" data={STATUS_OPTIONS} value={status}
            onChange={v => setStatus((v || 'active') as ZoneStatus)} />
        </Group>

        <Group grow>
          <ColorInput label="Color" value={color} onChange={setColor} />
          <div>
            <Text size="sm" fw={500} mb={4}>Fill Opacity</Text>
            <Slider value={opacity} onChange={setOpacity} min={0} max={1} step={0.05}
              marks={[{ value: 0, label: '0%' }, { value: 0.5, label: '50%' }, { value: 1, label: '100%' }]} />
          </div>
        </Group>

        <Textarea label="Notes" placeholder="Optional description or operational notes"
          value={notes} onChange={e => setNotes(e.currentTarget.value)} rows={3} />

        <Group justify="flex-end" mt="sm">
          <Button variant="light" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Save Zone</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
